<template>
  <div class="ai-report-detail-container pb-3">
    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5 my-5">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <!-- 极致紧凑详情卡片 -->
    <div v-else-if="report" class="detail-card shadow-sm rounded-lg overflow-hidden border">
      <!-- 紧凑头部 -->
      <div class="detail-header px-3 py-1 text-white">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center overflow-hidden flex-grow-1">
            <b-icon icon="arrow-left" class="cursor-pointer mr-2 back-icon" @click="$router.go(-1)"></b-icon>
            <div class="d-flex flex-column flex-md-row align-items-md-center overflow-hidden">
              <span class="font-weight-bold mr-md-2 text-truncate report-title-text">{{ report.title }}</span>
              <div class="d-flex align-items-center opacity-8 x-small mt-0 info-bar">
                <span class="mr-2 d-none d-sm-inline"><b-icon icon="person" class="mr-1"></b-icon>{{ report.username || 'AI' }}</span>
                <span class="mr-2"><b-icon icon="clock" class="mr-1"></b-icon>{{ report.created_time }}</span>
                <span><b-icon icon="hand-thumbs-up" class="mr-1"></b-icon>{{ report.likes || 0 }}</span>
              </div>
            </div>
          </div>
          <div class="ml-2 flex-shrink-0">
            <b-icon icon="share" class="cursor-pointer share-icon" @click="shareReport"></b-icon>
          </div>
        </div>
      </div>

      <!-- 紧凑正文 -->
      <div class="detail-body px-3 py-2">
        <article class="report-content" v-html="formattedContent" @click="handleContentClick"></article>

        <!-- 紧凑图片 -->
        <div v-if="images.length" class="report-images mt-2 pt-1 border-top">
          <div v-for="(img, idx) in images" :key="idx" class="mb-1 text-center">
            <img :src="img" class="img-fluid rounded border" style="max-height: 600px;" />
          </div>
        </div>
        
        <!-- 底部交互 - 极致紧凑 -->
        <div class="mt-2 pt-1 border-top d-flex justify-content-center">
          <b-button :variant="isLiked ? 'primary' : 'outline-primary'" size="sm" class="rounded-pill px-3 py-0" style="height: 20px; font-size: 0.7rem;" @click="toggleLike">
            <b-icon :icon="isLiked ? 'hand-thumbs-up-fill' : 'hand-thumbs-up'" class="mr-1" font-scale="0.8"></b-icon>
            {{ isLiked ? '已赞' : '有用' }}
          </b-button>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <p class="text-muted">未找到报告</p>
    </div>

    <!-- 股票K线侧边栏 -->
    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="sidebarDateMark" />
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'

export default {
  name: 'WonderAiReportDetail',
  components: {
    StockInfoSidebar
  },
  data() {
    return {
      reportId: this.$route.params.id,
      report: null,
      loading: true,
      isLiked: false,
      // 侧边栏相关
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: ''
    }
  },
  computed: {
    formattedContent() {
      if (!this.report?.content) return ''
      let c = this.report.content
        // 1. 预处理：移除板块标题前的多余换行，确保布局紧凑
        .replace(/\n+(?=概念：|核心逻辑：|催化事件：|核心股票:|其他股票:)/g, '\n')
        .replace(/\n/g, '<br>')
        .replace(/概念：(<br>)?/g, '<span class="section-badge theme-badge">题材 <span class="badge-arrow">→</span></span><strong class="text-primary">')
        .replace(/核心逻辑：(<br>)?/g, '</strong><span class="section-badge logic-badge">逻辑 <span class="badge-arrow">→</span></span>')
        .replace(/催化事件：(<br>)?/g, '<span class="section-badge catalyst-badge">催化 <span class="badge-arrow">→</span></span>')
        .replace(/核心股票:\s*(<br>)?/g, '<span class="section-badge stock-badge">核心标的:</span>')
        .replace(/其他股票:\s*(<br>)?/g, '<span class="section-badge other-badge">其他标的:</span>')
        .replace(/\[(.*?)\]/g, (match, content) => {
          // 提取名称和代码，例如 [中际旭创(300308) +1.17%]
          const stockMatch = content.match(/^(.*?)\((\d{6})\)/)
          let dataAttrs = ''
          if (stockMatch) {
            const name = stockMatch[1]
            const code = stockMatch[2]
            dataAttrs = `data-code="${code}" data-name="${name}"`
          }
          const isUp = content.includes('+')
          const colorClass = isUp ? 'text-danger' : (content.includes('-') ? 'text-success' : 'text-primary')
          return `<span class="stock-tag ${colorClass} ${dataAttrs ? 'cursor-pointer' : ''}" ${dataAttrs}>${content}</span>`
        })
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-dark">$1</strong>')
      
      return c
    },
    images() {
      if (!this.report?.images) return []
      try {
        const imgs = typeof this.report.images === 'string' 
          ? JSON.parse(this.report.images) 
          : this.report.images
        return Array.isArray(imgs) ? imgs : [imgs]
      } catch (e) {
        return []
      }
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    loadDetail() {
      this.loading = true
      request_zzquant({
        url: `/v3/ai-report/detail/${this.reportId}`,
        method: 'GET'
      })
      .then(res => {
        this.report = res?.data?.data || null
      })
      .catch(err => {
        console.error('Load report detail error:', err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    handleContentClick(e) {
      // 通过事件委托处理 v-html 中的点击
      const target = e.target.closest('.stock-tag')
      if (target && target.dataset.code) {
        this.openStockSidebar(target.dataset.code, target.dataset.name)
      }
    },
    openStockSidebar(code, name) {
      if (!code) return
      this.sidebarSymbolCode = code
      this.sidebarSymbolName = name || ''
      // 传递报告日期作为标记，sidebar 内部会自动格式化并标注 K 线位置
      this.sidebarDateMark = this.report ? this.report.created_time : ''
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },
    toggleLike() {
      this.isLiked = !this.isLiked
      if (this.report) {
        this.report.likes = (this.report.likes || 0) + (this.isLiked ? 1 : -1)
      }
    },
    shareReport() {
      const url = window.location.href
      navigator.clipboard.writeText(url).then(() => {
        this.$bvToast.toast('链接已复制', { variant: 'info', solid: true, appendToast: true, toaster: 'b-toaster-top-center' })
      })
    }
  }
}
</script>

<style scoped>
.ai-report-detail-container {
  max-width: 900px;
  margin: 0 auto;
}

.detail-card {
  border-color: #eee !important;
}

.detail-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}
.cursor-pointer:hover {
  opacity: 0.7;
}

.report-title-text {
  font-size: 1rem;
  max-width: 100%;
}

.x-small {
  font-size: 0.75rem;
}

.opacity-8 {
  opacity: 0.85;
}

.report-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
}

/* 内容板块样式 */
.report-content >>> .section-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  height: 22px;
  line-height: 22px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 800;
  margin-right: 10px;
  vertical-align: middle;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.report-content >>> .badge-arrow {
  margin-left: 4px;
  font-family: serif;
  font-weight: bold;
}

.report-content >>> .theme-badge { background-color: #007bff; color: #fff; }
.report-content >>> .logic-badge { background-color: #20bf6b; color: #fff; }
.report-content >>> .catalyst-badge { background-color: #eb3b5a; color: #fff; }
.report-content >>> .stock-badge { background-color: #4b6584; color: #fff; }
.report-content >>> .other-badge { background-color: #778ca3; color: #fff; }

.report-content >>> .stock-tag {
  display: inline-block;
  padding: 0 4px;
  margin: 1px 2px;
  background-color: #f8f9fa;
  border-radius: 3px;
  font-weight: 600;
  font-size: 0.9rem;
}

.report-content >>> .cursor-pointer {
  cursor: pointer;
  text-decoration: underline dotted;
}

.report-content >>> .text-danger { color: #d63031 !important; }
.report-content >>> .text-success { color: #27ae60 !important; }

/* 响应式调整 */
@media (min-width: 768px) {
  .info-bar {
    border-left: 1px solid rgba(255, 255, 255, 0.3);
    padding-left: 1rem;
  }
}

.detail-body {
  background-color: #fff;
}

/* 暗黑模式适配 */
body.theme-dark .detail-card {
  border-color: #333 !important;
}
body.theme-dark .detail-body {
  background-color: #1a1a1a !important;
}
body.theme-dark .report-content {
  color: #b0b3b8;
}
body.theme-dark .report-content >>> .text-primary {
  color: #58a6ff !important;
}
body.theme-dark .report-content >>> .text-dark {
  color: #f0f6fc !important;
}
body.theme-dark .report-content >>> .stock-tag {
  background-color: #21262d;
}
body.theme-dark .report-content >>> .text-danger {
  color: #ff7b72 !important;
}
body.theme-dark .report-content >>> .text-success {
  color: #3fb950 !important;
}
body.theme-dark .report-content >>> .section-badge {
  opacity: 0.85;
}
body.theme-dark .report-content >>> .theme-badge { background-color: #1f6feb; }
body.theme-dark .report-content >>> .logic-badge { background-color: #238636; }
body.theme-dark .report-content >>> .catalyst-badge { background-color: #da3633; }
body.theme-dark .report-content >>> .stock-badge { background-color: #30363d; }
body.theme-dark .report-content >>> .other-badge { background-color: #21262d; }
</style>
